import type {
  OrganizationNotificationChannel as GqlOrganizationNotificationChannel,
  OrganizationNotificationSetting as GqlOrganizationNotificationSetting,
  OrganizationNotificationSettingItem as GqlOrganizationNotificationSettingItem,
  OrganizationNotificationType as GqlOrganizationNotificationType,
} from "../../graphql";

export type OrganizationNotificationSetting =
  `${GqlOrganizationNotificationSetting}`;

export type OrganizationNotificationChannel =
  `${GqlOrganizationNotificationChannel}`;

export type OrganizationNotificationType = `${GqlOrganizationNotificationType}`;

export const ORGANIZATION_NOTIFICATION_SETTING_OPTIONS: Array<{
  value: OrganizationNotificationSetting;
  label: string;
}> = [
  { value: "USER_CHOICE", label: "User Choice" },
  { value: "FORCE_ENABLE", label: "Force Enable" },
  { value: "FORCE_DISABLE", label: "Force Disable" },
];

export const ORGANIZATION_NOTIFICATION_FIELDS = {
  interviewRecapSubscriptionEmail: {
    notificationType: "INTERVIEW_RECAP",
    channel: "EMAIL",
  },
  interviewRecapSubscriptionSlack: {
    notificationType: "INTERVIEW_RECAP",
    channel: "SLACK",
  },
  interviewRecapSubscriptionTeams: {
    notificationType: "INTERVIEW_RECAP",
    channel: "TEAMS",
  },
  interviewPrepSubscriptionEmail: {
    notificationType: "INTERVIEW_PREP",
    channel: "EMAIL",
  },
  interviewPrepSubscriptionSlack: {
    notificationType: "INTERVIEW_PREP",
    channel: "SLACK",
  },
  interviewPrepSubscriptionTeams: {
    notificationType: "INTERVIEW_PREP",
    channel: "TEAMS",
  },
  weeklyRecapSubscriptionEmail: {
    notificationType: "WEEKLY_RECAP",
    channel: "EMAIL",
  },
  weeklyRecapSubscriptionSlack: {
    notificationType: "WEEKLY_RECAP",
    channel: "SLACK",
  },
  weeklyRecapSubscriptionTeams: {
    notificationType: "WEEKLY_RECAP",
    channel: "TEAMS",
  },
  monthlyAnalyticsSubscriptionEmail: {
    notificationType: "MONTHLY_ANALYTICS",
    channel: "EMAIL",
  },
  monthlyAnalyticsSubscriptionTeams: {
    notificationType: "MONTHLY_ANALYTICS",
    channel: "TEAMS",
  },
  callShareSubscriptionEmail: {
    notificationType: "CALL_SHARE",
    channel: "EMAIL",
  },
  callShareSubscriptionSlack: {
    notificationType: "CALL_SHARE",
    channel: "SLACK",
  },
  callShareSubscriptionTeams: {
    notificationType: "CALL_SHARE",
    channel: "TEAMS",
  },
  callNoteSubscriptionEmail: {
    notificationType: "CALL_NOTE",
    channel: "EMAIL",
  },
  callNoteSubscriptionSlack: {
    notificationType: "CALL_NOTE",
    channel: "SLACK",
  },
  callNoteSubscriptionTeams: {
    notificationType: "CALL_NOTE",
    channel: "TEAMS",
  },
  morningDigestSubscriptionEmail: {
    notificationType: "MORNING_DIGEST",
    channel: "EMAIL",
  },
  morningDigestSubscriptionSlack: {
    notificationType: "MORNING_DIGEST",
    channel: "SLACK",
  },
  morningDigestSubscriptionTeams: {
    notificationType: "MORNING_DIGEST",
    channel: "TEAMS",
  },
  coachingSubscriptionEmail: {
    notificationType: "COACHING",
    channel: "EMAIL",
  },
  coachingSubscriptionSlack: {
    notificationType: "COACHING",
    channel: "SLACK",
  },
  coachingSubscriptionTeams: {
    notificationType: "COACHING",
    channel: "TEAMS",
  },
  candidateRediscoverySubscriptionEmail: {
    notificationType: "CANDIDATE_REDISCOVERY",
    channel: "EMAIL",
  },
  candidateRediscoverySubscriptionTeams: {
    notificationType: "CANDIDATE_REDISCOVERY",
    channel: "TEAMS",
  },
} as const;

export type OrganizationNotificationFieldName =
  keyof typeof ORGANIZATION_NOTIFICATION_FIELDS;

export type OrganizationNotificationSettingsMap = Record<
  string,
  OrganizationNotificationSetting
>;

export type OrganizationNotificationSettingItem = Pick<
  GqlOrganizationNotificationSettingItem,
  "notificationType" | "channel" | "setting"
>;

export const buildOrganizationNotificationSettingsMap = (
  settings: OrganizationNotificationSettingItem[] | undefined
): OrganizationNotificationSettingsMap => {
  const map: OrganizationNotificationSettingsMap = {};
  settings?.forEach((setting) => {
    map[`${setting.notificationType}:${setting.channel}`] = setting.setting;
  });
  return map;
};

export const getOrganizationNotificationSetting = (
  settingsMap: OrganizationNotificationSettingsMap,
  notificationType: OrganizationNotificationType,
  channel: OrganizationNotificationChannel
): OrganizationNotificationSetting =>
  settingsMap[`${notificationType}:${channel}`] ?? "USER_CHOICE";
